import { Container, Graphics, Sprite } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t } from '@/i18n';
import { getTexture } from '@/assets/resolver';
import { buildCollectionChip } from './shop-tooltips';
import { buildShopButton } from './shop-actions';
import { chamferPanel, label } from './widgets';
import type { FocusItem, ShopState } from './shop-types';

interface Hooks {
  isSel(item: FocusItem): boolean;
  interactive(node: Container, item: FocusItem): void;
}

/** Card collection panel + chips (+N overflow marker). */
export function buildCollection(
  s: ShopState, x: number, y: number, w: number, h: number,
  maxChips: number, chipPos: (i: number) => [number, number], hooks: Hooks,
): Container {
  const c = new Container();
  const panel = new Graphics();
  chamferPanel(panel, w, h, { fill: 0x110e1b, stroke: 0x2a2440, notch: 14 });
  panel.position.set(x, y);
  c.addChild(panel);
  const title = label(
    `${t('shop.collectionTitle')} — ${s.collection.reduce((sum, e) => sum + e.count, 0)}`,
    12, COLORS.uiAccent, 2,
  );
  title.position.set(x + 14, y + 10);
  c.addChild(title);

  s.collection.slice(0, maxChips).forEach((entry, i) => {
    const item: FocusItem = { kind: 'chip', cardId: entry.card.id };
    const chip = buildCollectionChip(entry.card, entry.count, hooks.isSel(item));
    chip.position.set(...chipPos(i));
    hooks.interactive(chip, item);
    c.addChild(chip);
  });
  if (s.collection.length > maxChips) {
    const more = label(`+${s.collection.length - maxChips}`, 13, 0x9aa0a6);
    more.anchor.set(1, 1);
    more.position.set(x + w - 12, y + h - 6);
    c.addChild(more);
  }
  return c;
}

/** Next-sector preview (B5) + CONTINUE button, right column bottom. */
export function buildNextSector(s: ShopState, x: number, w: number, hooks: Hooks): Container {
  const c = new Container();
  const next = label(t('shop.next'), 12, 0x9aa0a6, 3);
  next.position.set(x + 2, 548);
  const arena = label(s.arenaName, 15, COLORS.playerCyan, 1);
  arena.position.set(x + 2, 564);
  if (arena.width > w) arena.scale.set(w / arena.width);
  c.addChild(next, arena);

  // Boss preview (docs/12 B5): dark silhouette + level + wave count
  if (s.next) {
    const sil = new Sprite(getTexture(s.next.bodyKey));
    sil.anchor.set(0.5);
    sil.tint = 0x141022;
    sil.scale.set(46 / Math.max(sil.texture.width, sil.texture.height));
    sil.position.set(x + 24, 610);
    c.addChild(sil);
    const bname = label(s.next.name, 13, 0xd5d9de, 1);
    bname.anchor.set(0, 0.5);
    bname.position.set(x + 50, 602);
    if (bname.width > w - 54) bname.scale.set((w - 54) / bname.width);
    const parts = [t('shop.level', { n: s.next.level })];
    if (s.next.waves > 0) parts.push(t('shop.nextWaves', { n: s.next.waves }));
    const binfo = label(parts.join('  ·  '), 12, 0x9aa0a6, 1);
    binfo.anchor.set(0, 0.5);
    binfo.position.set(x + 50, 620);
    c.addChild(bname, binfo);
  }

  const cont = buildShopButton(
    t('shop.continueBtn'), w, hooks.isSel({ kind: 'continue' }), true, true,
  );
  cont.position.set(x, 636);
  hooks.interactive(cont, { kind: 'continue' });
  c.addChild(cont);
  return c;
}
